// ─────────────────────────────────────────────
//  Field — label + input + error, WCAG 2.2 AA (D-11)
//  ALWAYS pairs <label htmlFor> with <input id>. Errors surface
//  via role="alert" and the input's aria-describedby points to the
//  error element. Focus ring lives on the <input>, not the wrapper.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import tokens from "../theme/tokens";

const { color, space, radius, font } = tokens;

export default function Field({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  helperText,
  error,
  disabled = false,
  required = false,
}) {
  const [focus, setFocus] = useState(false);
  const errorId = error ? id + "-error" : undefined;
  const helperId = helperText ? id + "-helper" : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(" ") || undefined;

  const inputStyle = {
    ...styles.input,
    borderColor: error
      ? color.accent.error
      : focus
      ? color.accent.primary
      : color.border.default,
    background: disabled ? color.surface.raised : color.surface.card,
    ...(focus ? styles.inputFocus : null),
    ...(error && focus ? { outlineColor: color.accent.error } : null),
  };

  return (
    <div style={styles.group}>
      {/* Label always present in the DOM, even when disabled (opacity only).
          The required asterisk is a sibling (not inside <label>) so the input's
          accessible name stays exactly `label` and the `required` attribute is
          the semantic source of truth. */}
      <div style={{ ...styles.labelRow, opacity: disabled ? 0.6 : 1 }}>
        <label htmlFor={id} style={styles.label}>{label}</label>
        {required ? <span style={styles.req} aria-hidden="true">*</span> : null}
      </div>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        aria-describedby={describedBy}
        aria-invalid={error ? "true" : undefined}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={inputStyle}
      />
      {helperText && !error ? (
        <div id={helperId} style={styles.helper}>{helperText}</div>
      ) : null}
      {error ? (
        <div id={errorId} role="alert" style={styles.error}>{error}</div>
      ) : null}
    </div>
  );
}

const styles = {
  group: { marginBottom: space.md },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginBottom: space.xs + 2,
  },
  label: {
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    fontFamily: font.family,
  },
  req: { color: color.accent.error, fontWeight: font.weight.bold },
  input: {
    width: "100%",
    color: color.text.primary,
    border: "1px solid " + color.border.default,
    borderRadius: radius.sm,
    padding: "10px 14px",
    // 16px, NOT font.size.body (14). iOS auto-zooms any focused text field
    // under 16px, and the zoom PERSISTS across navigation — which is what the
    // user reported as the app "moving side to side". The mobile shell was
    // converted to INPUT_FONT_SIZE for exactly this, but Field is the shared
    // input for the LOGIN screen, which the shell falls through to on every
    // cold start and after every Sign off. The one screen every iOS user
    // touches was the one screen the conversion missed.
    fontSize: 16,
    fontFamily: font.family,
    boxSizing: "border-box",
  },
  // Visible focus-visible ring on the input itself (D-11).
  inputFocus: {
    outline: "2px solid " + color.accent.primary,
    outlineOffset: "1px",
  },
  helper: {
    fontSize: font.size.label,
    color: color.text.muted,
    marginTop: space.xs + 2,
    fontFamily: font.family,
  },
  error: {
    background: color.surface.error,
    border: "1px solid " + color.accent.error,
    // Ink, not accent.error: #dc2626 on this ground is 4.41:1 and FAILS AA for
    // normal text (tokens.js used to assert 4.95:1 and pass — it was wrong, and
    // four screens had been consolidated onto that sentence). accent.error stays
    // correct as the BORDER above; it is only as text that it falls short.
    color: color.surface.errorInk,
    borderRadius: radius.sm,
    padding: "8px 12px",
    fontSize: font.size.label,
    marginTop: space.xs + 2,
    fontFamily: font.family,
  },
};
